import type { FC, MouseEvent, ReactNode } from "react";
import { useState } from "react";
import { Box, Link as MuiLink, Typography } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import { useTranslation } from "@privacy/translations";
import type { TranslationKey } from "@privacy/translations";
import { hasErrorCode } from "../api/errors";
import { downloadFileMutationOptions } from "../api/files";
import { ErrorMessage } from "../dynamicForm/inlineForm/ErrorMessage";
import { downloadBlob } from "../shared/downloadBlob";
import type { DocumentReference, FileReference } from "../dynamicForm/types";
import { formatFileSize } from "../shared/formatFileSize";
import { HoverPopover, useHoverPopover } from "../shared/HoverPopover";
import { DownloadIcon, ExternalLinkIcon, FileTextIcon } from "../shared/icons";
import { FOCUS_VISIBLE_STYLES, formatHref } from "./Link";
import { CircularProgress, HACK_alignIconWithLabel } from "@privacy/ui";

export const formatDocumentReference = (reference: DocumentReference) => {
  const detail = reference.kind === "file" ? reference.fileName : reference.url;
  return detail && detail !== reference.label ? `${reference.label} - ${detail}` : reference.label;
};

type DocumentLinkProps = {
  reference: DocumentReference;
  downloadFileMutation: UseMutationResult<Response, Error, string>;
  sx?: SxProps<Theme>;
};

export const DocumentLink: FC<DocumentLinkProps> = ({ reference, downloadFileMutation, sx }) => {
  const hoverPopover = useHoverPopover();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [isClicked, setIsClicked] = useState(false);

  return (
    <>
      <MuiLink
        {...hoverPopover.anchorProps}
        ref={setAnchor}
        aria-haspopup={undefined}
        component="button"
        type="button"
        underline="hover"
        onClick={event => {
          event.stopPropagation();
          setIsClicked(true);
        }}
        onMouseLeave={() => {
          hoverPopover.anchorProps.onMouseLeave();
          setIsClicked(false);
        }}
        sx={[fileLinkStyles, { "&:focus-visible": FOCUS_VISIBLE_STYLES }, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        <FileLabel label={reference.label} />
      </MuiLink>
      <HoverPopover
        {...hoverPopover.popoverProps}
        open={hoverPopover.popoverProps.open || isClicked}
        anchorEl={anchor}
        onClose={() => setIsClicked(false)}
      >
        {reference.kind === "file" ? (
          <FileCard
            file={reference}
            action={<DownloadButton file={reference} downloadFileMutation={downloadFileMutation} />}
          />
        ) : (
          <UrlCard label={reference.label} url={reference.url} />
        )}
      </HoverPopover>
    </>
  );
};

export const DocumentPreview: FC<{ reference: DocumentReference }> = ({ reference }) => {
  const { anchorProps, popoverProps } = useHoverPopover();
  const { t } = useTranslation();
  const downloadFileMutation = useMutation(downloadFileMutationOptions());
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <Typography
        component="span"
        {...anchorProps}
        ref={setAnchor}
        aria-haspopup={undefined}
        sx={[
          fileLinkStyles,
          { gap: 1, lineHeight: theme => theme.spacing(4), color: theme => theme.palette.text.secondary },
        ]}
      >
        <FileLabel label={reference.label} />
      </Typography>
      <HoverPopover
        {...popoverProps}
        anchorEl={anchor}
        slotProps={{ ...popoverProps.slotProps, transition: { onExited: downloadFileMutation.reset } }}
      >
        {reference.kind === "file" ? (
          <FileCard
            file={reference}
            action={<DownloadButton file={reference} downloadFileMutation={downloadFileMutation} />}
            error={
              <>
                {downloadFileMutation.isPending &&
                  hasErrorCode(downloadFileMutation.failureReason, "FILE_SCAN_PENDING") && (
                    <ErrorMessage variant="loading" errorMessage={t("Document.Card.ScanInProgress")} />
                  )}
                {downloadFileMutation.isError && (
                  <ErrorMessage errorMessage={t(getDownloadErrorMessageKey(downloadFileMutation.error))} />
                )}
              </>
            }
          />
        ) : (
          <UrlCard label={reference.label} url={reference.url} />
        )}
      </HoverPopover>
    </>
  );
};

export const getDownloadErrorMessageKey = (error: unknown): TranslationKey => {
  if (hasErrorCode(error, "FILE_SCAN_PENDING")) return "Document.Card.ScanPending";
  if (hasErrorCode(error, "FILE_SCAN_FAILED")) return "Document.Card.ScanFailed";
  return "Document.Card.DownloadError";
};

const FileCard: FC<{ file: FileReference; action?: ReactNode; error?: ReactNode }> = ({ file, action, error }) => (
  <DocumentCard label={file.label} details={<FileDetails file={file} />} action={action} error={error} />
);

type DownloadButtonProps = { file: FileReference; downloadFileMutation: UseMutationResult<Response, Error, string> };

const DownloadButton: FC<DownloadButtonProps> = ({ file, downloadFileMutation }) => {
  const { t } = useTranslation();

  return (
    <MuiLink
      component="button"
      type="button"
      underline="hover"
      onClick={(event: MouseEvent) => {
        event.stopPropagation();
        if (downloadFileMutation.isPending) return;
        downloadFileMutation.mutate(file.fileId, { onSuccess: response => downloadBlob(response, file.fileName) });
      }}
      sx={cardActionStyles}
    >
      {t("Document.Card.Download")}
      {downloadFileMutation.isPending ? <CircularProgress size={16} /> : <DownloadIcon size={16} />}
    </MuiLink>
  );
};

const UrlCard: FC<{ label: string; url: string }> = ({ label, url }) => {
  const { t } = useTranslation();

  return (
    <DocumentCard
      label={label}
      details={<Detail term={t("Document.Card.Address")}>{url}</Detail>}
      action={
        <MuiLink
          href={formatHref(url)}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          onClick={event => event.stopPropagation()}
          sx={cardActionStyles}
        >
          {t("Document.Card.OpenLink")}
          <ExternalLinkIcon size={16} />
        </MuiLink>
      }
    />
  );
};

const fileLinkStyles = {
  display: "inline-flex",
  minWidth: 0,
  maxWidth: "100%",
  alignItems: "flex-start",
  gap: 1.5,
  textAlign: "left",
  lineHeight: 1,
  fontSize: (theme: Theme) => theme.typography.pxToRem(14),
  color: (theme: Theme) => theme.palette.text.primary,
} satisfies SxProps<Theme>;

const FileIcon: FC = () => (
  <Box component="span" sx={{ display: "flex", alignItems: "center", height: "1lh", flexShrink: 0 }}>
    <FileTextIcon size={16} />
  </Box>
);

const FileLabel: FC<{ label: string }> = ({ label }) => (
  <>
    <FileIcon />
    <Box
      component="span"
      sx={{
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        ...HACK_alignIconWithLabel,
      }}
    >
      {label}
    </Box>
  </>
);

const cardActionStyles: SxProps<Theme> = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
  color: theme => theme.palette.primary.main,
  fontSize: theme => theme.typography.pxToRem(14),
  fontWeight: 600,
};

type DocumentCardProps = { label: string; details: ReactNode; action?: ReactNode; error?: ReactNode };

const DocumentCard: FC<DocumentCardProps> = ({ label, details, action, error }) => (
  <Box sx={{ py: 2, width: "20rem", maxWidth: "100%" }}>
    <Typography
      component="div"
      sx={{
        px: 2,
        display: "flex",
        gap: 1,
        mb: 1.5,
        overflowWrap: "anywhere",
        fontSize: theme => theme.typography.pxToRem(14),
        fontWeight: 600,
      }}
    >
      <FileIcon />
      {label}
    </Typography>
    <Box
      component="dl"
      sx={{ m: 0, px: 2, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 3, rowGap: 0.5 }}
    >
      {details}
    </Box>
    {action && (
      <Box sx={{ mt: 1.5, px: 2, pt: 1.5, borderTop: theme => `1px solid ${theme.palette.divider}` }}>{action}</Box>
    )}
    {error && <Box sx={{ pr: 2 }}>{error}</Box>}
  </Box>
);

const FileDetails: FC<{ file: FileReference }> = ({ file }) => {
  const { t, language } = useTranslation();

  return (
    <>
      <Detail term={t("Document.Card.File")}>{file.fileName}</Detail>
      <Detail term={t("Document.Card.Size")}>{formatFileSize(file.size, language)}</Detail>
    </>
  );
};

const Detail: FC<{ term: string; children: ReactNode }> = ({ term, children }) => (
  <>
    <Typography component="dt" sx={{ fontSize: theme => theme.typography.pxToRem(12), color: "text.secondary" }}>
      {term}
    </Typography>
    <Typography component="dd" sx={{ m: 0, fontSize: theme => theme.typography.pxToRem(12), overflowWrap: "anywhere" }}>
      {children}
    </Typography>
  </>
);
